import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/** Rango de fechas para históricos (cuando se conecte la lógica en el padre). */
export interface HistoryAnalyticsDateRange {
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-history-analytics-date-filter',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './history-analytics-date-filter.component.html',
  styleUrl: './history-analytics-date-filter.component.scss',
})
export class HistoryAnalyticsDateFilterComponent implements OnInit {
  activePreset: 'today' | '7days' | '30days' | 'currentMonth' | 'custom' = '7days';
  startDate = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().slice(0, 10);
  endDate = new Date().toISOString().slice(0, 10);

  /** Emitirá el rango cuando los atajos e inputs estén cableados. */
  @Output() rangeChange = new EventEmitter<HistoryAnalyticsDateRange>();

  private appliedRange: HistoryAnalyticsDateRange | null = null;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.changePreset('7days');
  }

  get appliedRangeLabel(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'es';
    return formatHistoryRangeLabel(this.appliedRange, lang);
  }

  changePreset(preset: 'today' | '7days' | '30days' | 'currentMonth' | 'custom'): void {
    this.activePreset = preset;
    if (preset === 'custom') {
      return;
    }

    switch (preset) {
      case 'today':
        this.publishRange({
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString(),
        });
        break;
      case '7days':
        this.publishRange({
          startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
          endDate: new Date().toISOString(),
        });
        break;
      case '30days':
        this.publishRange({
          startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString(),
          endDate: new Date().toISOString(),
        });
        break;
      case 'currentMonth':
        this.publishRange({
          startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
          endDate: new Date().toISOString(),
        });
        break;
    }
  }

  applyCustomRange(): void {
    this.publishRange({
      startDate: this.startDate,
      endDate: this.endDate,
    });
  }

  private publishRange(range: HistoryAnalyticsDateRange): void {
    this.appliedRange = range;
    this.rangeChange.emit(range);
  }
}

/** Fecha o rango que se está mostrando. Un mismo día se enseña una sola vez. */
function formatHistoryRangeLabel(range: HistoryAnalyticsDateRange | null | undefined, lang: string): string {
  const start = formatRangeDay(range?.startDate, lang);
  const end = formatRangeDay(range?.endDate, lang);
  if (!start || !end) {
    return '';
  }
  return start === end ? start : `${start} – ${end}`;
}

/** `YYYY-MM-DD` se lee en hora local; un ISO completo conserva su instante. */
function formatRangeDay(value: string | undefined, lang: string): string {
  if (!value) {
    return '';
  }
  const date = value.includes('T')
    ? new Date(value)
    : new Date(+value.slice(0, 4), +value.slice(5, 7) - 1, +value.slice(8, 10));
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(lang || 'es', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

